import React from 'react'
import { Tooltip } from 'react-tooltip'
import { DefaultErrorGroup, ErrorGroup, ErrorGroupState, isErrorGroupAcknowledgable, IssueType } from '../../domain/Error'
import Link from '../../helper/link'

interface Props {
    navigator: any
    errorGroup: DefaultErrorGroup
    updateErrorState: (url: string) => void
    loading: boolean
    acknowledgeFeatureActive: boolean
    isElevated: boolean | null
}

const PRIVILEGED_ROLE_REQUIRED_TOOLTIP = 'You need at least a privileged role to perform this action'

interface StateLink {
    route: string
    routeArguments: any
    title: string
    buttonType: string
    tooltip: string
    issueTypes: string[]
    simpleLink?: boolean
    disabled?: boolean
    confirmModal?: React.ReactNode
}

interface State {
    confirmLink: StateLink | null
}

function isNonIgnoreableExceptionType(exceptionType: string): boolean {
    return (
        exceptionType === 'Error' ||
        exceptionType === 'Exception' ||
        exceptionType === 'Error' ||
        exceptionType === 'TypeError' ||
        exceptionType === 'ClosedGeneratorException' ||
        exceptionType === 'ErrorException' ||
        exceptionType === 'ArgumentCountError' ||
        exceptionType === 'ArithmeticError' ||
        exceptionType === 'AssertionError' ||
        exceptionType === 'DivisionByZeroError' ||
        exceptionType === 'CompileError' ||
        exceptionType === 'ParseError' ||
        exceptionType === 'ValueError' ||
        exceptionType === 'UnhandledMatchError' ||
        exceptionType === 'FiberError' ||
        exceptionType === 'BadFunctionCallException' ||
        exceptionType === 'BadMethodCallException' ||
        exceptionType === 'DomainException' ||
        exceptionType === 'InvalidArgumentException' ||
        exceptionType === 'LengthException' ||
        exceptionType === 'LogicException' ||
        exceptionType === 'OutOfBoundsException' ||
        exceptionType === 'OutOfRangeException' ||
        exceptionType === 'OverflowException' ||
        exceptionType === 'RangeException' ||
        exceptionType === 'RuntimeException' ||
        exceptionType === 'UnderflowException' ||
        exceptionType === 'UnexpectedValueException'
    )
}

export class IssueWorkflowButtonGroup extends React.Component<Props, State> {
    public state: State = {
        confirmLink: null,
    }

    private closeConfirm = () => this.setState({ confirmLink: null })

    private confirmAction = () => {
        const link = this.state.confirmLink
        this.setState({ confirmLink: null })

        if (link !== null) {
            this.props.updateErrorState(this.props.navigator.path(link.route, link.routeArguments))
        }
    }

    private onLinkClick(link: StateLink): (() => void) | undefined {
        if (link.disabled) {
            return undefined
        }

        if (link.confirmModal) {
            return () => this.setState({ confirmLink: link })
        }

        return () => this.props.updateErrorState(this.props.navigator.path(link.route, link.routeArguments))
    }

    render() {
        return (
            <>
                <div className="btn-group">
                    {this.possibleStatusChanges(this.props.errorGroup).map((link, key) =>
                        link.simpleLink ? (
                            <Link
                                key={key}
                                route={link.route}
                                params={link.routeArguments}
                                data-tooltip-id="errorWorkflowTooltips"
                                data-tooltip-content={link.tooltip}
                                className={'btn btn-' + link.buttonType + (link.disabled ? ' is-disabled' : '')}
                                aria-disabled={link.disabled ? true : undefined}
                                style={link.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                            >
                                {link.title}
                            </Link>
                        ) : (
                            <button
                                key={key}
                                onClick={this.onLinkClick(link)}
                                className={'btn btn-' + link.buttonType + (link.disabled ? ' is-disabled' : '')}
                                data-tooltip-id="errorWorkflowTooltips"
                                data-tooltip-content={link.tooltip}
                                disabled={this.props.loading}
                                aria-disabled={link.disabled ? true : undefined}
                                style={link.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                            >
                                {link.title}
                            </button>
                        ),
                    )}
                </div>
                <Tooltip id="errorWorkflowTooltips" place="bottom-start" className="tw-react-tooltip" />
                {this.state.confirmLink !== null ? this.renderConfirmModal(this.state.confirmLink) : null}
            </>
        )
    }

    private renderConfirmModal(link: StateLink) {
        const style: React.CSSProperties = { display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', textAlign: 'left' }
        return (
            <div className="modal fade in" role="dialog" style={style}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button type="button" className="close" onClick={this.closeConfirm} aria-label="Close">
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <h4 className="modal-title">{'Confirm ' + link.title}</h4>
                        </div>
                        <div className="modal-body">{link.confirmModal}</div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-default" onClick={this.closeConfirm}>
                                Cancel
                            </button>
                            <button type="button" className={'btn btn-' + link.buttonType} onClick={this.confirmAction}>
                                {link.title}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    private possibleStatusChanges(errorGroup: ErrorGroup): StateLink[] {
        const openAction: StateLink = {
            route: 'XhprofErrorBundle.Errors.changeStatus',
            routeArguments: { error: errorGroup.id, status: ErrorGroupState.OPEN },
            title: 'Re-Open',
            buttonType: 'default',
            tooltip: 'Reopen this issue',
            issueTypes: [IssueType.ERROR, IssueType.SLOWSQL, IssueType.NOTICE, IssueType.WARNING, IssueType.DEPRECATED],
        }

        const notifyToggleAction: StateLink = {
            route: 'XhprofErrorBundle.Errors.toggleNotify',
            routeArguments: { error: errorGroup.id },
            title: errorGroup.notify ? 'Mute' : 'Unmute',
            buttonType: 'default',
            tooltip: errorGroup.notify
                ? 'Do not send notifications when this issue occurs.'
                : 'Re-enable notifications when this issue occurs.',
            issueTypes: [IssueType.ERROR, IssueType.SLOWSQL],
        }

        const acknowledgeAction: StateLink = {
            route: errorGroup.acknowledged ? 'XhprofErrorBundle.Errors.unacknowledge' : 'XhprofErrorBundle.Errors.acknowledge',
            routeArguments: { error: errorGroup.id },
            title: errorGroup.acknowledged ? 'Unacknowledge' : 'Acknowledge',
            buttonType: 'default',
            tooltip: errorGroup.acknowledged
                ? 'Resume sending repeating notifications for this issue.'
                : 'Acknowledge this issue. This will prevent repeating notifications being sent',
            issueTypes: [IssueType.ERROR],
        }

        const acknowledgeDisabledAction: StateLink = {
            route: 'XhprofAlertBundle.Configuration.list',
            routeArguments: {},
            title: 'Acknowledge',
            buttonType: 'default',
            tooltip:
                'Acknowledging errors is not available. Click on this button and configure the "New Error" alert to send notifications for "Unacknowledged Errors".',
            issueTypes: [IssueType.ERROR],
            simpleLink: true,
            disabled: true,
        }

        const requiresPrivilegedRole = !this.props.isElevated

        const notErrorAction: StateLink = {
            route: 'XhprofErrorBundle.Errors.changeStatus',
            routeArguments: { error: errorGroup.id, status: ErrorGroupState.IGNORED },
            title: 'Ignore',
            buttonType: 'default',
            tooltip: requiresPrivilegedRole ? PRIVILEGED_ROLE_REQUIRED_TOOLTIP : 'Ignore occurrences of this error',
            issueTypes: [IssueType.ERROR, IssueType.SLOWSQL],
            disabled: requiresPrivilegedRole,
        }

        const incidentArchiveAction: StateLink = {
            route: 'XhprofAlertBundle.Incident.archive',
            routeArguments: { incidentId: errorGroup.id, s: this.props.navigator.current.query.s },
            title: 'Archive',
            buttonType: 'default',
            tooltip: 'Archive this incident.',
            issueTypes: [IssueType.INCIDENT],
        }

        const incidentConfigAction: StateLink = {
            route: 'XhprofAlertBundle.Configuration.editCheck',
            routeArguments: { checkId: errorGroup.checkId },
            title: 'Config',
            buttonType: 'default',
            tooltip: 'Configure this notification.',
            issueTypes: [IssueType.INCIDENT],
            simpleLink: true,
        }

        const links: StateLink[] = []

        if (errorGroup.status === ErrorGroupState.OPEN) {
            links.push({
                route: 'XhprofErrorBundle.Errors.changeStatus',
                routeArguments: { error: errorGroup.id, status: ErrorGroupState.RESOLVED },
                title: 'Resolve',
                buttonType: 'default',
                tooltip: 'Mark this issue as resolved across all environments. It will re-open automatically when it occurs again.',
                issueTypes: [IssueType.ERROR, IssueType.SLOWSQL, IssueType.NOTICE, IssueType.WARNING, IssueType.DEPRECATED],
            })

            links.push(notErrorAction)
            links.push(notifyToggleAction)
            links.push(incidentArchiveAction)
            links.push(incidentConfigAction)
        }

        if (errorGroup.status === ErrorGroupState.RESOLVED) {
            links.push(openAction)
            links.push(notErrorAction)
            links.push(notifyToggleAction)
            links.push(incidentConfigAction)
        }

        if (isErrorGroupAcknowledgable(this.props.errorGroup)) {
            links.push(this.props.acknowledgeFeatureActive ? acknowledgeAction : acknowledgeDisabledAction)
        }

        if (errorGroup.status === ErrorGroupState.IGNORED) {
            if (errorGroup.level === 'error') {
                if (!isNonIgnoreableExceptionType(errorGroup.exceptionType) && !errorGroup.isOnDefaultAlwaysIgnoreList) {
                    // Cannot mark error groups as "Always Ignore" when its a Fatal Error/Exception
                    links.push({
                        route: 'XhprofErrorBundle.Errors.ignore',
                        routeArguments: { error: errorGroup.id },
                        title: 'Always Ignore',
                        buttonType: 'danger',
                        tooltip: requiresPrivilegedRole
                            ? PRIVILEGED_ROLE_REQUIRED_TOOLTIP
                            : 'Ignore all issues of this type ' + errorGroup.exceptionType,
                        issueTypes: [IssueType.ERROR],
                        disabled: requiresPrivilegedRole,
                        confirmModal: (
                            <p>
                                When you globally ignore this exception <code>{errorGroup.exceptionType}</code> then all errors across this
                                project that use this exception class will automatically be ignored. This is regardless of where in the
                                code, in which service or context it is thrown, when it has this exception class name it is being ignored.
                            </p>
                        ),
                    })
                } else if (errorGroup.isOnDefaultAlwaysIgnoreList) {
                    links.push({
                        route: 'XhprofErrorBundle.IgnoredExceptions.list',
                        routeArguments: {},
                        title: 'Configure',
                        buttonType: 'default',
                        tooltip: 'Review and configure always ignored exception settings.',
                        issueTypes: [IssueType.ERROR],
                        simpleLink: true,
                    })
                }
            }

            if (!errorGroup.isOnDefaultAlwaysIgnoreList) {
                links.push(openAction)
            }
        }

        if (errorGroup.status === ErrorGroupState.ALWAYS_IGNORED) {
            if (!errorGroup.isOnDefaultAlwaysIgnoreList) {
                links.push({
                    route: 'XhprofErrorBundle.Errors.unignore',
                    routeArguments: { error: errorGroup.id },
                    title: 'Unignore Always',
                    buttonType: 'default',
                    tooltip: 'Do not ignore all issues of this type anymore.',
                    issueTypes: [IssueType.ERROR],
                })
            }
            links.push(notifyToggleAction)
        }

        return links.filter((value: StateLink) => value.issueTypes.indexOf(errorGroup.level) >= 0)
    }
}
